import type { EChartsOption } from 'echarts';

import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { chartLayout } from '../config/chartLayout';
import { sliderVisuals } from '../config/chartVisuals';
import { INSIDE_DATA_ZOOM_ID, SLIDER_DATA_ZOOM_ID } from '../config/componentIds';

type BuildNavigationOptionParams = {
  xAxisIndexes: number[];
  theme: TimeSeriesChartTheme;
};

/**
 * Two ECharts data zooms back the navigation: an inside one for wheel and drag on the plot, and a
 * slider under the track.
 *
 * Zooming revealing more detail, and range-track bars that span the viewport surviving it, are
 * both handled ourselves: the renderer thins each line to its drawing budget, in detail inside the
 * current window and coarsely outside it (see getRenderableLineSeriesData / useVisibleRange), and
 * the range track's custom series is clipped by pixel position, not by data filtering. ECharts' own
 * `filterMode` therefore stays `none` deliberately: any filtering mode drops points strictly outside
 * the window with no way to keep a boundary neighbour, which breaks a line's continuity right at the
 * plot's edge. With the data already budget-sized, filtering would not make navigation faster either.
 */
const DATA_ZOOM_FILTER_MODE = 'none';

export function buildNavigationOption({
  xAxisIndexes,
  theme,
}: BuildNavigationOptionParams): Pick<EChartsOption, 'dataZoom'> {
  return {
    dataZoom: [
      {
        id: INSIDE_DATA_ZOOM_ID,
        type: 'inside',
        xAxisIndex: xAxisIndexes,
        filterMode: DATA_ZOOM_FILTER_MODE,
        // The wheel only zooms and panning stays on drag. With both on the wheel, ECharts zooms around
        // the cursor and then shifts the window by the same notch, so the zoom drifts off the cursor.
        zoomOnMouseWheel: true,
        moveOnMouseWheel: false,
        moveOnMouseMove: true,
      },
      {
        id: SLIDER_DATA_ZOOM_ID,
        type: 'slider',
        xAxisIndex: xAxisIndexes,
        filterMode: DATA_ZOOM_FILTER_MODE,
        left: chartLayout.sliderInset.left,
        right: chartLayout.sliderInset.right,
        bottom: chartLayout.sliderBottom,
        height: chartLayout.sliderHeight,
        showDetail: false,
        brushSelect: false,
        borderRadius: theme.slider.borderRadius,
        borderColor: theme.slider.borderColor,
        backgroundColor: theme.slider.backgroundColor,
        fillerColor: theme.slider.fillerColor,
        // A faint preview of the data makes the window position readable at a glance.
        dataBackground: {
          lineStyle: { color: theme.slider.dataShadowColor, width: sliderVisuals.dataShadowLineWidth },
          areaStyle: { color: theme.slider.dataShadowColor, opacity: sliderVisuals.dataShadowAreaOpacity },
        },
        selectedDataBackground: {
          lineStyle: { color: theme.slider.borderColor, width: sliderVisuals.dataShadowLineWidth },
          areaStyle: { color: theme.slider.fillerColor },
        },
        handleStyle: {
          color: theme.slider.handleColor,
          borderColor: theme.slider.handleBorderColor,
          borderWidth: sliderVisuals.handleBorderWidth,
        },
        moveHandleStyle: {
          opacity: 0,
        },
      },
    ],
  };
}
